import classNames from 'classnames';
import css from './Table.module.scss';
import { FaSort, FaSortAmountUpAlt, FaSortAmountDown } from 'react-icons/fa';
import React, { memo, useMemo, useRef } from 'react';
import clone from 'just-clone';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { SortColumn } from '../../../API/APIObjects';
import { SortOrder } from '../../../API/Enums';
import { formatTableData } from '../../../CommonUtils/Formatter';
import { RowsPerPageOptions } from '../../../Contexts/FilterProvider/FilterProvider.types';
import { useLocalization, useLocale, Locales } from '../../../Contexts/Localization';
import { GMIconButton } from '../../Buttons/IconButton';
import { VIRTUALIZATION_THRESHOLD, GMAutocomplete } from '../../Inputs/Autocomplete';
import { GMSelectOption, GMSelect, SelectItemsFromArray } from '../../Inputs/Select';
import { GMTooltip } from '../../Tooltip/Tooltip';
import { ColumnData, ColumnType, DEFAULT_ROWS_PER_PAGE, GMTableProps, RowData } from '..';
import { GoMotoComponent, GoMotoFunction } from '../../../API/GoMoto.types';
import { GMGrid } from '../../Grid/Grid';

/**
 * Table component where sorting and pagination is controlled via API calls
 */
const Table: GoMotoComponent<GMTableProps> = memo(({
    cellPadding = 'normal',
    sortIconPlacement = 'end',
    ...props
}) => {
    const Localization = useLocalization();
    const locale: Locales = useLocale();
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const page: number = props.pagination?.page ?? 1;
    const rowsPerPage: number = props.pagination?.rowsPerPage ?? DEFAULT_ROWS_PER_PAGE;
    const sortColumns: SortColumn[] = props.sorting?.sortColumns ?? [];
    const lastPage: number = props.pagination?.totalCount ? Math.ceil(props.pagination.totalCount / rowsPerPage) : 1;
    const rowsPerPageOptions: number[] = Object.values(RowsPerPageOptions);
    const paginationOptions = useMemo<GMSelectOption[]>(() => {
        return [...new Array<GMSelectOption>(lastPage)].map((_, index) => {
            const value = (index + 1).toString();
            return {
                value: value,
                label: value,
            };
        });
    }, [lastPage]);

    const onColumnClick: GoMotoFunction<[number], void> = (colId) => {
        if (props.sorting) {
            const newSortColumns: SortColumn[] = clone(sortColumns);
            const sortColumnIndex: number = newSortColumns.findIndex((c) => c.id === colId);

            /**
             * Sorting goes from Unsorted -> Ascending -> Descending -> Unsorted...
             */
            if (sortColumnIndex > -1) {
                if (newSortColumns[sortColumnIndex].sortOrder === SortOrder.asc) {
                    /**
                     * Clicking on a column always makes it the primary sort
                     */
                    newSortColumns.splice(sortColumnIndex, 1);
                    newSortColumns.unshift({
                        id: colId,
                        sortOrder: SortOrder.desc,
                    });
                }
                else {
                    newSortColumns.splice(sortColumnIndex, 1);
                }
            }
            else {
                newSortColumns.unshift({
                    id: colId,
                    sortOrder: SortOrder.asc,
                });
            }

            props.sorting.onSort(newSortColumns);

            scrollToTopOfTable();
        }
    };

    const scrollToTopOfTable: VoidFunction = () => {
        if (tableWrapperRef.current) {
            tableWrapperRef.current.scrollTop = 0;
        }
    };

    /**
     * Written as a functional component to fix a bug with the tooltip where
     * it would remain displayed when a loading overlay was shown
     */
    const SortIcon: React.FC<{ colId: number; }> = (({ colId }) => {
        const sortColumnIndex: number = sortColumns.findIndex((c) => c.id === colId);
        const sortIconClassName: string = classNames(css.sortIcon, css.flex, { [css.afterLabel]: sortIconPlacement === 'afterLabel' });

        if (sortColumnIndex > -1) {
            const ascending: boolean = sortColumns[sortColumnIndex].sortOrder === SortOrder.asc;

            return (
                <GMTooltip text={ascending ? Localization.Ascending : Localization.Descending}>
                    <div className={sortIconClassName}>
                        <span className={css.sortNumber}>
                            {sortColumnIndex + 1}
                        </span>
                        {ascending ?
                            <FaSortAmountUpAlt /> :
                            <FaSortAmountDown />
                        }
                    </div>
                </GMTooltip>
            );
        }

        return (
            <GMTooltip text={Localization.Unsorted}>
                <div className={sortIconClassName}>
                    <FaSort />
                </div>
            </GMTooltip>
        );
    });

    const onPageChange = (newPage: number) => {
        props.pagination && props.pagination.onPageChange(newPage, rowsPerPage);
        scrollToTopOfTable();
    };

    const onRowsPerPageChange = (value: string) => {
        let newRowsPerPage: number = +value;
        if (!isNaN(newRowsPerPage)) {
            props.pagination && props.pagination.onPageChange(1, newRowsPerPage);
        }

        scrollToTopOfTable();
    };

    const columnJustification = (column: ColumnData) => {
        if (column.cellJustification) {
            return column.cellJustification;
        }

        switch (column.type) {
            case ColumnType.Boolean:
            case ColumnType.BooleanYN:
            case ColumnType.BooleanCheck:
            case ColumnType.BooleanString:
                return 'center';
            case ColumnType.Currency:
                return 'right';
            default:
                return 'left';
        }
    };

    /*
     * Shared by the pinned top rows and the bottom total row so the two can't drift apart; only the bottom (legacy) row sticks
     * and keeps the header's look. Top rows get the lighter summaryRow treatment so they read as data, not a second header.
     */
    const renderSummaryRows: GoMotoFunction<
        [RowData[] | undefined, ColumnData[] | undefined, 'topSummary' | 'legacy', number[] | undefined],
        JSX.Element[] | undefined
    > = (
        rows,
        columns,
        variant,
        emphasizedRowIndexes,
    ) => {
        const isTopSummary: boolean = variant === 'topSummary';

        return rows?.map((row: RowData, rowIndex: number) => {
            return (
                <tr
                    className={classNames(
                        css.row,
                        isTopSummary ? css.summaryRow : css.headerRow,
                        isTopSummary && emphasizedRowIndexes?.includes(rowIndex) ? css.summaryRowEmphasized : '',
                    )}
                    style={!isTopSummary ? {
                        position: 'sticky',
                        bottom: '0',
                    } : undefined}
                    key={`table-${props.name}-row-${rowIndex}`}
                >
                    {columns?.map((column: ColumnData, colIndex: number) => {
                        return (
                            <th
                                key={`table-${props.name}-row-${column.label}-${column.id}-${rowIndex}-${colIndex}`}
                                className={classNames(css.cell, css.totalRowPadding, column.cellClassName)}
                                onClick={column.disableSorting ? undefined : () => onColumnClick(column.id)}
                                style={{
                                    width: `${column.percentWidth}%`,
                                    minWidth: column.minWidth,
                                    textAlign: isTopSummary ? columnJustification(column) : (column.headerJustification ?? undefined),
                                }}
                            >
                                {formatTableData(
                                    row[column.rowDataKey],
                                    column.type,
                                    column.replaceWithifEmpty,
                                    false,
                                    `table-${props.name}-row-${column.label}-${column.id}-${rowIndex}-${colIndex}-data`,
                                    locale,
                                )}
                            </th>
                        );
                    })}
                </tr>
            );
        });
    };

    return (
        <div className={css.root}>
            {props.additionalInformation ? (
                <GMGrid xs={12}>
                    {props.additionalInformation}
                </GMGrid>
            ) : null}
            {props.pagination ?
                <div className={classNames(css.pagination, css.flex)}>
                    <div className={css.flexGrow}>
                        {props.pagination.totalCount > 0 ?
                            Localization.formatString(
                                Localization.DisplayingRecords,
                                <b>{((page - 1) * rowsPerPage) + 1}</b>,
                                <b>{Math.min(page * rowsPerPage, props.pagination.totalCount)}</b>,
                                <>{props.pagination.totalCount}</>,
                            ) :
                            Localization.NoRecordsFound
                        }
                    </div>
                    <div className={css.flex}>
                        <div>
                            {Localization.ResultsPerPage}
                        </div>
                        <div>
                            <GMSelect
                                variant={'standard'}
                                value={rowsPerPage.toString()}
                                options={SelectItemsFromArray(rowsPerPageOptions)}
                                onChange={onRowsPerPageChange}
                            />
                        </div>
                        <div className={css.flex}>
                            <GMIconButton
                                onClick={() => onPageChange(1)}
                                tooltip={Localization.FirstPage}
                                disabled={page === 1}
                            >
                                <KeyboardDoubleArrowLeftIcon />
                            </GMIconButton>
                            <GMIconButton
                                onClick={() => onPageChange(page - 1)}
                                tooltip={Localization.PreviousPage}
                                disabled={page === 1}
                            >
                                <KeyboardArrowLeftIcon />
                            </GMIconButton>
                            {paginationOptions.length > VIRTUALIZATION_THRESHOLD ?
                                <div style={{ width: `calc(65px + ${paginationOptions.length.toString().length + 'ch'})` }}>
                                    <GMAutocomplete
                                        variant={'standard'}
                                        value={page.toString()}
                                        options={paginationOptions}
                                        onChange={(value) => onPageChange(+value)}
                                        clearable={false}
                                        numericOnly
                                        noOptionsText={''}
                                    />
                                </div> :
                                <GMSelect
                                    variant={'standard'}
                                    value={page.toString()}
                                    options={paginationOptions}
                                    onChange={(value) => onPageChange(+value)}
                                />
                            }
                            <GMIconButton
                                onClick={() => onPageChange(page + 1)}
                                tooltip={Localization.NextPage}
                                disabled={page === lastPage}
                            >
                                <KeyboardArrowRightIcon />
                            </GMIconButton>
                            <GMIconButton
                                onClick={() => onPageChange(lastPage)}
                                tooltip={Localization.LastPage}
                                disabled={page === lastPage}
                            >
                                <KeyboardDoubleArrowRightIcon />
                            </GMIconButton>

                        </div>
                    </div>
                </div>
                : null
            }
            <div ref={tableWrapperRef} className={css.tableWrapper}>
                <table
                    className={classNames(
                        css.table,
                        props.themed ? css.themed : '',
                        props.sorting ? css.sortable : '',
                        props.borderless ? css.borderless : '',
                    )}
                >
                    <thead>
                        <tr className={classNames(
                            css.headerRow,
                            css.row,
                        )}
                        >
                            {props.columns.map((column: ColumnData, index: number) => {
                                return (
                                    <th
                                        className={classNames(css.cell, props.sorting && !column.disableSorting ? css.clickable : '', {
                                            [css.normalPadding]: cellPadding === 'normal',
                                            [css.extraPadding]: cellPadding === 'extra',
                                        })}
                                        onClick={column.disableSorting ? undefined : () => onColumnClick(column.id)}
                                        style={{
                                            width: `${column.percentWidth}%`,
                                            minWidth: column.minWidth,
                                            textAlign: column.headerJustification ?? undefined,
                                        }}
                                        key={`table-${props.name}-col-${column.label}-${index}`}
                                    >
                                        {props.sorting && !column.disableSorting ?
                                            <div className={css.flex}>
                                                <div className={sortIconPlacement === 'end' ? css.flexGrow : undefined}>
                                                    {column.label}
                                                </div>
                                                <SortIcon colId={column.id} />
                                            </div> :
                                            column.label
                                        }
                                    </th>
                                );
                            })}
                        </tr>
                        {props.topSummaryRows ?
                            renderSummaryRows(
                                props.topSummaryRows.rows,
                                props.topSummaryRows.columns,
                                'topSummary',
                                props.topSummaryRows.emphasizedRowIndexes,
                            ) :
                            null
                        }
                    </thead>
                    <tbody>
                        {props.rows?.map((row: RowData, rowIndex: number) => {
                            return (
                                <tr
                                    className={classNames(css.bodyRow, css.row, props.onRowClick ? css.clickable : '')}
                                    key={`table-${props.name}-row-${rowIndex}`}
                                >
                                    {props.columns.map((column: ColumnData, colIndex: number) => {
                                        return (
                                            <td
                                                key={`table-${props.name}-row-${column.label}-${column.id}-${rowIndex}-${colIndex}`}
                                                align={columnJustification(column)}
                                                className={classNames(
                                                    css.cell,
                                                    column.onCellClick || props.onRowClick ? css.clickable : '',
                                                    column.rowSecondaryDataKey ? css.innerRowAlignment : '',
                                                    column.cellClassName,
                                                    {
                                                        [css.normalPadding]: cellPadding === 'normal',
                                                        [css.extraPadding]: cellPadding === 'extra',
                                                    },
                                                )}
                                                style={{
                                                    width: `${column.percentWidth}%`,
                                                    fontWeight: column.fontWeight ? column.fontWeight : 'normal',
                                                    minWidth: column.minWidth,
                                                }}
                                                onClick={() => (column.onCellClick ? column.onCellClick(row) : props.onRowClick ? props.onRowClick(row) : null)}
                                            >
                                                <div
                                                    className={classNames(
                                                        css.content,
                                                        column.lineLimit && column.lineLimit > 0 ? css.ellipsis : '',
                                                    )}
                                                    style={
                                                        column.lineLimit && column.lineLimit > 0 ? {
                                                            lineClamp: column.lineLimit,
                                                            WebkitLineClamp: column.lineLimit,
                                                        } : undefined
                                                    }
                                                >
                                                    {column.customCellFormatter ? column.customCellFormatter(row[column.rowDataKey], row) : formatTableData(
                                                        row[column.rowDataKey],
                                                        column.type,
                                                        column.replaceWithifEmpty,
                                                        false,
                                                        `table-${props.name}-row-${column.label}-${column.id}-${rowIndex}-${colIndex}-data`,
                                                        locale,
                                                    )}
                                                    {column.rowSecondaryDataKey ?
                                                        <>
                                                            <br />
                                                            {/* Inline span so the secondary line can be styled on its own; it inherits the cell when no class is given. */}
                                                            <span className={column.secondaryDataClassName}>
                                                                {column.customCellFormatter ? column.customCellFormatter(row[column.rowSecondaryDataKey], row) : formatTableData(
                                                                    row[column.rowSecondaryDataKey],
                                                                    column.type,
                                                                    column.replaceWithifEmpty,
                                                                    false,
                                                                    `table-${props.name}-row-${column.label}-${column.id}-${rowIndex}-${colIndex}-secondarydata`,
                                                                    locale,
                                                                )}
                                                            </span>
                                                        </> : null
                                                    }
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                    <thead>
                        {renderSummaryRows(props.addTotalRow?.rows, props.addTotalRow?.columns, 'legacy', undefined)}
                    </thead>
                </table>
            </div>
        </div>
    );
});

export default Table;